/**
 * NO ACCESS dialog (web).
 *
 * Shown when a login succeeds but the account is linked to nothing — no
 * institute membership, and no platform-level grant.
 *
 * Before this, such a user was routed into the app anyway and landed on a shell
 * with no data and no explanation. Saying plainly that the account is not linked
 * to an institute is the difference between "the app is broken" and "ask your
 * institute to add you".
 *
 * The session is NOT opened: routeAfterLogin() is never called, so the user
 * stays on the login page rather than being dropped inside an empty app.
 *
 * shadcn + Tailwind + global.css tokens, no component CSS, no inline styles.
 */

import React from 'react';
import { ShieldAlert } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface NoAccessDialogProps {
  open: boolean;
  email?: string | null;
  /** On an institute's own domain: its name. The account exists; it is just
   *  not part of this institute (yet). */
  instituteName?: string;
  onOpenChange: (open: boolean) => void;
}

const NoAccessDialog: React.FC<NoAccessDialogProps> = ({
  open,
  email,
  instituteName,
  onOpenChange,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          </div>
          <DialogTitle className="text-center">No access yet</DialogTitle>
          <DialogDescription className="text-center">
            {instituteName
              ? `Your account is not part of ${instituteName} yet.`
              : "Your account is not linked to any institute, so there is nothing to show yet."}
          </DialogDescription>
        </DialogHeader>

        {email ? (
          <p className="text-center text-xs text-muted-foreground">{email}</p>
        ) : null}

        <p className="text-center text-sm text-muted-foreground">
          {instituteName
            ? `Ask ${instituteName} to add this email address, then sign in again. If you think this is a mistake, contact ${instituteName}.`
            : "Ask your institute to add this email address, then sign in again. If you think this is a mistake, contact your administrator."}
        </p>

        <Button className="w-full" onClick={() => onOpenChange(false)}>
          Back to sign in
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default NoAccessDialog;
